import { SingleShipmentDataExtended } from '@/apis/types';
import { RightArrowIcon, RightTailArrowIcon } from '@/assets/icons/icons';
import { Divider } from 'antd';
import React from 'react';
import { CustomTitle } from '../../../../components';
import { CardVisibilityState } from '../../MapExternalView';
import TrackingLinkReportTimeIssue from './TrackingLinkReportTimeIssue';
import UpdateDeliveryAddress from './UpdateDeliveryAddress';

interface ReportIssueCardProps {
  cardVisibilityState?: CardVisibilityState;
  handleCardActions?: (
    showCard: boolean,
    isOpen: boolean,
    name: string
  ) => void;
  shipment: Partial<SingleShipmentDataExtended>;
  token?: string;
  fetchSingleShipmentData?: (showSpinner: boolean) => void;
  refetchSharePageData?: () => void;
  isSharePage?: boolean;
}

const DEFAULT_ADDRESS_MESSAGE =
  "We'll contact the courier to update the address";
const DEFAULT_TIME_MESSAGE =
  "We'll share that preference with the courier to see if it can be accommodated";

const ReportIssueCard: React.FC<ReportIssueCardProps> = ({
  cardVisibilityState,
  handleCardActions,
  shipment,
  token,
  refetchSharePageData,
}) => {
  const reportIssueConfig = shipment?.report_issue_config;
  const addressEnabled = reportIssueConfig?.address_update_enabled ?? true;
  const timeEnabled = reportIssueConfig?.delivery_time_enabled ?? true;
  const askAddressChangeScope =
    reportIssueConfig?.ask_address_change_scope ?? true;

  const REPORT_ISSUES = [
    ...(addressEnabled && shipment?.to_location?.address?.trim()
      ? [
          {
            key: 'address',
            label: 'Update delivery address',
          },
        ]
      : []),
    ...(timeEnabled && shipment?.scheduled_delivery_date
      ? [
          {
            key: 'time',
            label: 'Suggest a different delivery time',
          },
        ]
      : []),
  ];

  const getReportIssueHeader = (name?: string) => {
    switch (name) {
      case 'time':
        return {
          title: 'Suggest a different delivery time',
          subTitle:
            reportIssueConfig?.delivery_time_message || DEFAULT_TIME_MESSAGE,
        };

      case 'address':
        return {
          title: 'Update delivery address',
          subTitle:
            reportIssueConfig?.address_update_message ||
            DEFAULT_ADDRESS_MESSAGE,
        };

      default:
        return {
          title: 'Report issue',
          subTitle: '',
        };
    }
  };
  const { title, subTitle } = getReportIssueHeader(cardVisibilityState?.name);

  const handleBack = () => {
    const { name } = cardVisibilityState;
    // If already on root
    if (!name) {
      handleCardActions(false, false, '');
      return;
    }
    // If inside sub-view (time/address) → go back to root view
    handleCardActions(true, false, '');
  };
  return (
    <>
      <div>
        <span className='cursor-pointer' onClick={() => handleBack()}>
          <RightTailArrowIcon />
        </span>

        <CustomTitle
          text={title}
          fontSize={16}
          fontWeight={600}
          lineHeight='20px'
          color='var(--text-primary)'
          marginBottom='0'
          className='report-issue-heading'
        />

        {subTitle && (
          <CustomTitle
            text={subTitle}
            fontSize={14}
            fontWeight={400}
            lineHeight='20px'
            color='var(--text-secondary)'
            marginTop='6px'
            marginBottom='16px'
          />
        )}

        <Divider style={{ margin: '8px 0' }} />

        {!cardVisibilityState?.name && (
          <div className='report-issue-options'>
            {REPORT_ISSUES.map((item) => (
              <div
                key={item.key}
                className='report-issue-card'
                onClick={() => handleCardActions(true, true, item.key)}
              >
                <span>{item.label}</span>
                <RightArrowIcon width={16} height={16} />
              </div>
            ))}
          </div>
        )}
      </div>
      {cardVisibilityState?.name == 'address' && (
        <UpdateDeliveryAddress
          address={shipment?.to_location?.address}
          onCancel={handleBack}
          externalToken={token}
          shipmentTimezone={shipment?.timezone}
          refetchSharePageData={refetchSharePageData}
          handleCardActions={handleCardActions}
          askAddressChangeScope={askAddressChangeScope}
        />
      )}
      {cardVisibilityState?.name == 'time' && (
        <TrackingLinkReportTimeIssue
          shipment={shipment}
          token={token}
          handleCardActions={handleCardActions}
          deliveryData={shipment?.scheduled_delivery_date}
          refetchSharePageData={refetchSharePageData}
        />
      )}
    </>
  );
};

export default ReportIssueCard;
